import { Popover } from "@app/components";
import { balanceAction } from "@app/constants";
import { ComboButton } from "@app/controls";
import type { DropdownItem } from "@app/entities";
import type { RootState } from "@app/redux";
import { type FormatNumberOptions, formatNumber } from "@app/utils";
import React, { memo, useMemo } from "react";
import { useSelector } from "react-redux";
import "./balance-card.css";
import "./balance-card.responsive.css";
import { FaInfoCircle } from "react-icons/fa";
import messages from "./messages";
import type { ViewProperties } from "./properties";

export const BalanceCardView = memo((props: ViewProperties) => {
	const { language } = useSelector(
		(rootState: RootState) => rootState.language,
	);

	// #region CONSTANTS
	const desktopNumberOptions = useMemo<FormatNumberOptions>(() => {
		return {
			currencyCode: messages[language].currencyCode,
			decimalPlaces: 2,
			groupSeparator: ",",
		};
	}, [language]);

	const mobileNumberOptions = useMemo<FormatNumberOptions>(() => {
		return {
			currencyCode: messages[language].currencyCode,
			decimalPlaces: props.balance >= 1000 ? 2 : 0,
			formatUnit: true,
		};
	}, [props.balance, language]);

	const colStyle = ["flex", "flex-col"].join(" ");

	const rowStyle = ["flex", "flex-row"].join(" ");

	const containerInnerStyle = [
		colStyle,
		"balance-card-inner",
		"mr-auto",
		"w-full",
	].join(" ");

	const containerOuterStyle = [
		rowStyle,
		"flex-wrap",
		"gap-x-4",
		"py-5.5",
		"px-5",
		"lg:px-7",
		"balance-card",
		"relative",
		"z-20",
		props.className,
	].join(" ");

	const bottomSectionStyle = [
		rowStyle,
		"font-primary-light",
		"gap-x-2",
		"justify-between",
		"whitespace-nowrap",
		"text-3xl",
		"leading-none",
	].join(" ");

	const topSectionStyle = [
		rowStyle,
		"gap-x-2",
		"items-center",
		"justify-between",
	].join(" ");

	const headingStyle = [
		rowStyle,
		"font-primary-bold",
		"gap-x-2",
		"balance-card-heading",
	].join(" ");

	const balanceLastUpdatedStyle = [
		"font-primary-regular",
		"text-sm",
		"balance-card-last-updated",
		"flex",
	].join(" ");

	const footnoteStyle = [
		"font-primary-regular",
		"leading-5",
		"-mt-2.5 mx-2.5",
		"p-2.5",
		"footnote",
	].join(" ");

	const tooltipStyle = ["opacity-50", "balance-card icon-filled", "ml-2"].join(
		" ",
	);

	const actionOptions = useMemo((): DropdownItem<string>[] => {
		return [
			{
				value: balanceAction.addFunds,
				label: messages[language].action.addFunds,
			},
			{
				value: balanceAction.withdraw,
				label: messages[language].action.withdrawSpecific,
			},
		];
	}, [language]);

	const getWarningTooltip = () => {
		return (
			<Popover
				toggleContent={<FaInfoCircle className={tooltipStyle} size="18px" />}
				popperContent={
					<div
						className={footnoteStyle}
						dangerouslySetInnerHTML={{
							__html: props.balanceLastUpdatedWarning,
						}}
					/>
				}
				showArrow={true}
				showOnHover={props.isDesktop}
				flip={true}
				placement="bottom"
				offset={[16, 16]}
			/>
		);
	};

	return (
		<div className={containerOuterStyle}>
			<div className={[containerInnerStyle].join(" ")}>
				<div className={topSectionStyle}>
					<div className={headingStyle}>{messages[language].heading}</div>
					<ComboButton
						className="balance-card-combo-button pb-0 z-0"
						buttonClassName="pb-1.5"
						fixedValue={
							actionOptions.length > 0 ? actionOptions[0].value : undefined
						}
						fixedValueHiddenInDropdown
						items={actionOptions}
						onClick={(value: string) => props.onBalanceAction(value)}
					/>
				</div>
				<div className={bottomSectionStyle}>
					<div className="balance-card-figure">
						{props.balance > 0
							? formatNumber(
									props.balance,
									props.isDesktop ? desktopNumberOptions : mobileNumberOptions,
								)
							: messages[language].defaultValue}
					</div>
				</div>
				{props.balanceLastUpdated && (
					<div className={balanceLastUpdatedStyle}>
						<div>{props.balanceLastUpdated}</div>
						{props.balanceLastUpdatedWarning && (
							<div>{getWarningTooltip()}</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
});
